import { getPdfScalingFactor } from '~/src/utils/pdfPageSizeUtils';

export const DEFAULT_ELEMENT_HEIGHT = 14;
export const DEFAULT_MULTILINE_ELEMENT_HEIGHT = 18;

function wrapText(context, text, x, y, line_width, line_height) {
  let line = '';
  const paragraphs = (text || '').split('\n');
  const fontSize = parseFloat(context.font);
  const lhDiff = line_height - fontSize;
  const minY = y; // Never draw above the starting position (clip boundary)
  if (lhDiff > 0 && lhDiff <= 6) {
    y += Math.log1p(lhDiff) * 3 - 2;
  } else if (lhDiff > 6) {
    // Same offset as lhDiff=6, then ramp steeper
    y += Math.log1p(6) * 3 - 3 + (lhDiff - 6) * 0.7;
  } else if (lhDiff < 0) {
    y -= (lhDiff + 2) / 2;
  }
  if (y < minY) y = minY + 1;

  for (let i = 0; i < paragraphs.length; i++) {
    const words = paragraphs[i].split(' ');
    for (let n = 0; n < words.length; n++) {
      const testLine = `${line + words[n]} `;
      const metrics = context.measureText(testLine);
      const testWidth = metrics.width;
      if (testWidth > line_width && n > 0) {
        context.fillText(line, x, y);
        line = `${words[n]} `;
        y += line_height;
      } else {
        line = testLine;
      }
    }
    context.fillText(line, x, y);
    y += line_height;
    line = '';
  }
}

export const getFileFromUrl = async (url, name) => {
  const response = await fetch(url);
  const data = await response.blob();
  return new File([data], name, {
    type:
      response.headers.get('content-type') ||
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

export const getPdfBlob = async function (
  webviewerInstance,
  downloadOnly = false,
) {
  const instanceViewer = webviewerInstance.docViewer;
  const doc = instanceViewer.getDocument();
  const annotManager = instanceViewer.getAnnotationManager();
  const annots = annotManager.getAnnotationsList();
  const originalMultilineAnnotations = [];
  const printOnlyMultilineInputs = [];
  const scaleFactor = getPdfScalingFactor(webviewerInstance);
  // When downloading, create a custom multiline annotation that draws a canvas
  // and manually implements line height for given text. THe breaking up of text
  // as per its length has to be done manually as well to fit within the width.
  if (downloadOnly) {
    annots.forEach(async (widgetAnnot) => {
      if (
        widgetAnnot.Subject == 'Widget' &&
        widgetAnnot.getField().flags.get('Multiline') &&
        widgetAnnot.getCustomData('lineHeight')
      ) {
        const { Annotations } = webviewerInstance;
        const currentFieldName = widgetAnnot.getField().name;
        const multilineWidgetExists = annots.filter(
          (a) => a.getCustomData('originalFieldName') === currentFieldName,
        );
        if (multilineWidgetExists.length > 0) {
          multilineWidgetExists[0].Height = JSON.parse(
            multilineWidgetExists[0].getCustomData('widgetAnnotOriginalHeight'),
          );
          multilineWidgetExists[0].textToSet = widgetAnnot.getField().value;
          annotManager.redrawAnnotation(multilineWidgetExists[0]);
          printOnlyMultilineInputs.push(multilineWidgetExists[0]);
          originalMultilineAnnotations.push(widgetAnnot);
          widgetAnnot.Height = 0;
        } else {
          class MultilineAnnotation extends Annotations.CustomAnnotation {
            constructor() {
              super('multilineannot'); // provide the custom XFDF element name
              this.Subject = 'CustomMultiLineCanvas';
            }

            draw(ctx) {
              const paddingLeft = 1 / scaleFactor;
              const paddingRight = 0;
              const x = this.X + paddingLeft;
              const y = this.Y;
              const maxWidth = this.Width - paddingLeft - paddingRight;

              // Clip to original widget bounds so overflow text is hidden
              const originalHeight = JSON.parse(
                this.getCustomData('widgetAnnotOriginalHeight'),
              );
              ctx.save();
              ctx.beginPath();
              ctx.rect(this.X, this.Y, this.Width, originalHeight);
              ctx.clip();

              const text = this.textToSet;
              const { lineHeight } = this;
              ctx.font = `${DEFAULT_ELEMENT_HEIGHT / scaleFactor}px Lato`;
              ctx.textBaseline = 'top';
              wrapText(ctx, text, x, y, maxWidth + 2, lineHeight);

              ctx.restore();
            }
          }

          MultilineAnnotation.prototype.elementName = 'multilineannot';
          annotManager.registerAnnotationType(
            MultilineAnnotation.prototype.elementName,
            MultilineAnnotation,
          );
          const customMultilinePrintOnlyAnnot = new MultilineAnnotation();
          customMultilinePrintOnlyAnnot.PageNumber =
            widgetAnnot.getPageNumber();
          customMultilinePrintOnlyAnnot.X = widgetAnnot.getX();
          customMultilinePrintOnlyAnnot.Y = widgetAnnot.getY();
          customMultilinePrintOnlyAnnot.setCustomData(
            'originalFieldName',
            currentFieldName,
          );
          customMultilinePrintOnlyAnnot.setCustomData(
            'widgetAnnotOriginalHeight',
            widgetAnnot.getHeight(),
          );
          customMultilinePrintOnlyAnnot.textToSet =
            widgetAnnot.getField().value;
          customMultilinePrintOnlyAnnot.lineHeight =
            JSON.parse(widgetAnnot.getCustomData('lineHeight')) /
            (scaleFactor * scaleFactor);

          customMultilinePrintOnlyAnnot.Width = widgetAnnot.getWidth();
          customMultilinePrintOnlyAnnot.Height =
            widgetAnnot.getHeight() * 1.1 + 2;

          /*
          Check whether a custom annotation has a height as 0.
          If yes, do not add it to annotation manager.
          Otherwise, a custom annotation whose height is 0 will appear as 
          a red Draft stamp on a downloaded file.
          In this case, we use the original multiline widget instead.
          https://github.com/mystacksco/issues/issues/1407
          */
          if (customMultilinePrintOnlyAnnot.Height > 0) {
            annotManager.addAnnotation(customMultilinePrintOnlyAnnot);
            printOnlyMultilineInputs.push(customMultilinePrintOnlyAnnot);
            originalMultilineAnnotations.push(widgetAnnot);
            widgetAnnot.Height = 0;
          }
        }
      }
    });
  }

  const xfdfString = await annotManager.exportAnnotations();
  /* 
  We've exported annotations to an XFDF string.
  So, we restore original multiline widgets right away because we want
  to minimize the time that original multiline widgets have a height of 0px.
  */

  if (downloadOnly) {
    // We can delete the multi-line annotations we created for the download, as they are no longer needed.
    annotManager.deleteAnnotations(printOnlyMultilineInputs);

    // We should also restore the height of the original annotations.
    originalMultilineAnnotations.forEach((annot) => {
      annot.Height = JSON.parse(
        annot.getCustomData('widgetAnnotOriginalHeight'),
      );
    });
  }

  const options = { xfdfString, flatten: true };
  const data = await doc.getFileData(options);
  const arr = new Uint8Array(data);
  const blob = new Blob([arr], { type: 'application/pdf' });

  return {
    blobData: blob,
    numberOfPages: doc.getPageCount(),
    documentId: +doc.getDocumentId(),
  };
};
